// store/slices/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios'
import DeviceService from '@/services/devices';
import localconfig from '@/config/local-config.json';

export interface Device {
  device_id?: string;
  name?: string;
  base_model: string;
  base_model_display_name: string;
  base_model_name?: string;
  category?: string;
  decommissioned: boolean;
  device_type: string;
  gtin_number?: string;
  has_components?: boolean;
  has_parent?: boolean;
  is_critical?: boolean;
  lifecycle_status?: string;
  location_id?: string;
  location_name?: string;
  long_description?: string;
  manufacturer?: string;
  manufacturer_name?: string;
  model_string?: string;
  notes?: string;
  parent_guid?: string;
  parent_serial_number?: string;
  parent_tag_no?: string;
  pdp_link?: string;
  serial_number?: string;
  stock_keeping_unit?: string;
  division_id?: string;
  store?: string;
  tag_no?: string;
  valve_size?: string;
  pt_rating?: string;
  site_id?: number;
  gdm_customer_number?: string;
  customer_name?: string;
  account_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  postal_code?: string;
  city?: string;
  state_prov?: string;
  country_dsc?: string;
  world_area_dsc?: string;
  authorized?: boolean;
  images?: any;
  videos?: any;
}

interface DeviceState {
  data: Device | null;
  loading: boolean;
  error: string | null;
  fetched: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  errorCode?: number;
  errorDetails?: unknown;

}

const initialState: DeviceState = {
  data: null,
  loading: false,
  error: null,
  fetched: false,
  status: 'idle',
  errorCode: undefined,
  errorDetails: undefined,

};

type ThunkArg = {
  deviceid: string;
  accessToken?: string
};

type RejectedPayload = {
  message: string;
  status?: number;
  details?: unknown;
};


/**
 * Normalize Axios error to {message, status, details}.
 */
function toRejectedPayload(err: unknown): RejectedPayload {
  const fallback: RejectedPayload = { message: 'Unknown error' };
  if (!err || typeof err !== 'object') return fallback;

  const axiosErr = err as AxiosError<any>;
  // Axios error shape: response?.status, response?.data, message
  const message =
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Request failed';

  // console.log('toRejectedPayload: extracted error message', axiosErr.toJSON?.());
  const status = axiosErr.response?.status;
  // const details = axiosErr.response?.data ?? axiosErr.toJSON?.() ?? undefined;
  const details = {
    name: axiosErr.name,
    code: axiosErr.code,
    message: axiosErr.message,
    status: axiosErr.response?.status,
    // config: axiosErr.config,
    responseData: axiosErr.response?.data,
  };

  return { message, status, details };
}

// Async thunk for fetching user data
export const fetchDeviceDetails = createAsyncThunk<Device, ThunkArg,{ rejectValue: RejectedPayload }>
('user/fetchDeviceDetails', async ({ deviceid, accessToken }, { rejectWithValue }) => {
    try {
      console.log('fetchDeviceDetails: starting fetch');

      const serviceObj = new DeviceService(undefined, accessToken);
      const resp = await serviceObj.getDetails({ deviceid: deviceid});
      console.log('fetchDeviceDetails: fetched details', resp);

      return resp;
      // return {
      //   id: resp.id,
      //   email: resp.email,
      //   superadmin: userDetails.is_superadmin,
      //   cdmContactId: userDetails.cdmContactId,
      //   cdmCompanyData: userDetails.cdm_company_data,
      //   firstname: userDetails.first_name,
      //   lastname: userDetails.last_name,
      //   role: userDetails.user_role
      // } as User;
    } catch (err) {
      return rejectWithValue(toRejectedPayload(err));
    }

  },
  {
    // condition to prevent duplicate requests
    condition: (_arg, { getState }) => {
      const state = getState() as { device: DeviceState };
          return state.device.status === 'idle' && !state.device.fetched;
    },
  }

);

const DeviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {
    clearDevice: (state) => {
      state.loading = false;
      state.data = null;
      state.error = null;
      state.fetched = false; // Reset fetched flag
      state.status = 'idle';
    },
    setDevice(state, action: PayloadAction<DeviceState>) {
      return action.payload; // replaces entire state
    },
    setImageAndVideos(state, action: PayloadAction<{images: any; videos: any}>) {
      console.log('DeviceDetails: setting images and videos', action.payload);
      if (state.data) {
        state.data.images = action.payload.images;
        state.data.videos = action.payload.videos;
      }
    },
    updateDeviceId(state, action: PayloadAction<string>) {
      console.log('DeviceSlice: ',action.payload);
      if (state.data) {
        state.data.device_id = action.payload;
      }
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceDetails.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeviceDetails.fulfilled, (state, action) => {
        const payloadData = action.payload;
        // console.log('deviceSlice: fetched device', payloadData);
        state.loading = false;
        state.data = payloadData as Device;
        state.fetched = true; // Mark as fetched
        state.status = 'succeeded';
      })
      .addCase(fetchDeviceDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        // state.error = action.error.message || 'Failed to fetch user';
        state.fetched = true; // Still mark as fetched to prevent retries

        const payload = action.payload as RejectedPayload | undefined;
        if (payload) {
          state.error = payload.message;
          state.errorCode = payload.status || 99;
          state.errorDetails = payload.details;
        } else {
          // Fallback to RTK's generic error
          state.error = action.error.message || 'Failed to fetch user';
        }

        // Optional: special handling for 401/403
        // if (state.errorCode === 401 || state.errorCode === 403) {
        //   // e.g., dispatch(logout) or mark a flag to trigger re-auth UI
        // }

      });
  },
});

export const { clearDevice, setDevice, updateDeviceId, setImageAndVideos } = DeviceSlice.actions;
export default DeviceSlice.reducer;